import { useMemo } from 'react';
import { useRooms } from './useRooms';

export interface NotificationInfo {
  hasNewMessages: boolean;
  unreadCount: number;
}

/**
 * Read-Hook für Benachrichtigungen basierend auf UUIDs.
 * Sucht Räume die die UUID im Namen enthalten und gibt deren Notification-Status zurück.
 * Reaktiv - triggert Re-Render bei Änderungen.
 */
export function useNotificationsByUuids(targetUuids: string[]): Map<string, NotificationInfo> {
  const rooms = useRooms();

  return useMemo(() => {
    const notifications = new Map<string, NotificationInfo>();
    if (!rooms.length || !targetUuids.length) return notifications;

    // Create a Map to store room info by UUID for O(1) lookup
    const roomsByUuid = new Map<string, (typeof rooms)[0]>();

    // First pass: organize rooms by UUID
    for (const room of rooms) {
      const roomName = room.name;
      if (!roomName) continue;

      // Find matching UUID efficiently
      const matchingUuid = targetUuids.find((uuid) => roomName.includes(uuid));
      if (matchingUuid) {
        roomsByUuid.set(matchingUuid, room);
      }
    }

    // Second pass: create notifications
    for (const uuid of targetUuids) {
      const room = roomsByUuid.get(uuid);
      if (room) {
        const unreadCount = room.getUnreadNotificationCount() ?? 0;
        notifications.set(uuid, {
          hasNewMessages: unreadCount > 0,
          unreadCount,
        });
      } else {
        // Set default values for UUIDs without rooms
        notifications.set(uuid, {
          hasNewMessages: false,
          unreadCount: 0,
        });
      }
    }

    return notifications;
  }, [rooms, targetUuids]);
}

/**
 * Read-Hook für die Gesamtanzahl ungelesener Nachrichten.
 * Kann bestimmte Raumtypen ein- oder ausschließen.
 * Reaktiv - triggert Re-Render bei Änderungen.
 *
 * @param options.includeRoomTypes - Nur Räume mit diesen Typen zählen (z.B. ['dm'])
 * @param options.excludeRoomTypes - Räume mit diesen Typen ausschließen (z.B. ['dm'])
 */
export function useTotalUnreadCount(
  options: { includeRoomTypes?: string[]; excludeRoomTypes?: string[] } = {},
): number {
  const rooms = useRooms();

  return useMemo(() => {
    return rooms.reduce((total, room) => {
      const roomType = room.name.split('_')[0];

      if (options.includeRoomTypes && options.includeRoomTypes.length > 0) {
        if (!options.includeRoomTypes.includes(roomType ?? '')) {
          return total;
        }
      }

      if (options.excludeRoomTypes && options.excludeRoomTypes.length > 0) {
        if (options.excludeRoomTypes.includes(roomType ?? '')) {
          return total;
        }
      }

      return total + (room.getUnreadNotificationCount() ?? 0);
    }, 0);
  }, [rooms, options.includeRoomTypes, options.excludeRoomTypes]);
}

export function useHasUnread(options: { includeRoomTypes?: string[]; excludeRoomTypes?: string[] }): boolean {
  const count = useTotalUnreadCount(options);
  return count > 0;
}
